import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import type { ServiceNode } from './productTree';
import toggleOff from '../../assets/product-toggle-off.svg';
import toggleOn from '../../assets/product-toggle-on.svg';

/** Keys the Cascades accordion's open state, alongside the service names. */
const CASCADES_SECTION = 'Cascades';

/** One of the lender's cascades — an order logic rule the user may or may not reach (OM-4022). */
export interface CascadeLeaf {
  id: string;
  name: string;
}

interface ProductAccordionTreeProps {
  tree: ServiceNode[];
  selected: Record<string, boolean>;
  onToggle: (urn: string) => void;
  /** When present, only these leaves render. Step 4 uses it to show just what Step 3 enabled. */
  visibleUrns?: string[];
  /** When true, leaves are disabled (out of the tab order, no onClick) — Step 5 uses it for the read-only tree. */
  readOnly?: boolean;
  /**
   * The lender's cascades, rendered as their own section after the services (OM-4022 AC2). Absent
   * or empty renders no section at all — the wizard passes none.
   */
  cascades?: CascadeLeaf[];
  /** Cascade id -> granted. Separate from `selected`: a cascade is a rule, not a product urn. */
  cascadesSelected?: Record<string, boolean>;
  onToggleCascade?: (id: string) => void;
}

export default function ProductAccordionTree({
  tree, selected, onToggle, visibleUrns, readOnly, cascades, cascadesSelected, onToggleCascade,
}: Readonly<ProductAccordionTreeProps>) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const visibleSet = visibleUrns ? new Set(visibleUrns) : null;
  const isVisible = (urn: string) => visibleSet === null || visibleSet.has(urn);

  return (
    <div className="space-y-3.5">
      {tree.map((serviceNode) => {
        const productTypes = serviceNode.productTypes
          .map((pt) => ({
            productType: pt.productType,
            providers: pt.providers
              .map((provider) => ({
                provider: provider.provider,
                products: provider.products.filter((p) => isVisible(p.urn)),
              }))
              .filter((provider) => provider.products.length > 0),
          }))
          .filter((pt) => pt.providers.length > 0);

        const selectedCount = productTypes.reduce(
          (serviceTotal, pt) =>
            serviceTotal +
            pt.providers.reduce(
              (typeTotal, provider) => typeTotal + provider.products.filter((p) => selected[p.urn]).length,
              0,
            ),
          0,
        );

        const isOpen = Boolean(expanded[serviceNode.service]);

        return (
          <div key={serviceNode.service} className="rounded-xl border border-border bg-surface p-6">
            <button
              type="button"
              className="flex w-full items-center justify-between text-left"
              onClick={() =>
                setExpanded((prev) => ({ ...prev, [serviceNode.service]: !prev[serviceNode.service] }))
              }
            >
              <span className="text-base font-semibold text-black dark:text-content">{serviceNode.service}</span>
              <span className="flex items-center gap-4 text-base font-medium text-content">
                <span><span className="font-bold text-brand-deep dark:text-brand">{selectedCount}</span> products selected</span>
                {isOpen ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
              </span>
            </button>

            {isOpen && (
              <div className="mt-6 space-y-4">
                {productTypes.map((pt) => (
                  <div key={pt.productType}>
                    <h4 className="text-base font-medium text-content">{pt.productType}</h4>
                    {pt.providers.map((provider) => (
                      <div key={provider.provider} className="mt-2">
                        <p className="text-sm text-content-secondary">{provider.provider}</p>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 pl-[30px]">
                          {provider.products.map((leaf) => {
                            const checked = Boolean(selected[leaf.urn]);
                            return (
                              <button
                                key={leaf.urn}
                                type="button"
                                role="switch"
                                aria-checked={checked}
                                aria-label={`${leaf.name}, ${provider.provider}`}
                                onClick={() => onToggle(leaf.urn)}
                                disabled={readOnly}
                                className="flex items-center gap-2 py-1 pr-3 text-base"
                              >
                                <img src={checked ? toggleOn : toggleOff} alt="" />
                                <span className="text-black dark:text-content">{leaf.name}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* The lender's cascades, in their own section after the services (OM-4022 AC2). */}
      {cascades && cascades.length > 0 && (
        <div className="rounded-xl border border-border bg-surface p-6">
          <button
            type="button"
            className="flex w-full items-center justify-between text-left"
            onClick={() => setExpanded((prev) => ({ ...prev, [CASCADES_SECTION]: !prev[CASCADES_SECTION] }))}
          >
            <span className="text-base font-semibold text-black dark:text-content">{CASCADES_SECTION}</span>
            <span className="flex items-center gap-4 text-base font-medium text-content">
              <span>
                <span className="font-bold text-brand-deep dark:text-brand">
                  {cascades.filter((cascade) => cascadesSelected?.[cascade.id]).length}
                </span>{' '}
                cascades selected
              </span>
              {expanded[CASCADES_SECTION] ? <ChevronUp className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
            </span>
          </button>

          {expanded[CASCADES_SECTION] && (
            <div className="mt-6 flex flex-wrap gap-x-3 gap-y-1">
              {cascades.map((cascade) => {
                const checked = Boolean(cascadesSelected?.[cascade.id]);
                return (
                  <button
                    key={cascade.id}
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={cascade.name}
                    onClick={() => onToggleCascade?.(cascade.id)}
                    disabled={readOnly}
                    className="flex items-center gap-2 py-1 pr-3 text-base"
                  >
                    <img src={checked ? toggleOn : toggleOff} alt="" />
                    <span className="text-black dark:text-content">{cascade.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
